import { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import { normalizeChannels, shortChannelLabel } from '../../../lib/broadcastChannels';
import {
  distinctRecipientCount,
  historyFailureLine,
  historySentLine,
  historySkippedLine,
} from '../../../lib/broadcastHistoryCounts';
import {
  deliveryChip,
  deliveryFailureBadges,
  deliveryRefetchInterval,
  deliveryReportsExpected,
  deliveryReportsOffNotice,
  deliverySummaryLine,
  historyRecipients,
} from '../../../lib/broadcastDelivery';
import { formatInZone, resolveTripTimeZone } from '../../../lib/tripTime';
import { audienceLabel, statusColor } from './audience';
import type { GroupBroadcastSummary } from '../../../types/groups';

export const BROADCAST_HISTORY_KEY = (groupId: string) => ['group-broadcasts', groupId];

function RecipientRow({
  recipient,
  reportsExpected,
}: {
  recipient: GroupBroadcastSummary['recipients'][number];
  reportsExpected: boolean;
}) {
  const delivery = deliveryChip(recipient, reportsExpected);
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>{recipient.guestName || 'Guest'}</Typography>
        {recipient.email || recipient.phone ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {[recipient.email, recipient.phone].filter(Boolean).join(' · ')}
          </Typography>
        ) : null}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        {recipient.error ? (
          <Typography variant="caption" color="error" sx={{ maxWidth: 160 }} noWrap title={recipient.error}>
            {recipient.error}
          </Typography>
        ) : null}
        {/* Rows are per (guest, channel); without this chip two rows for the same
            guest read as a duplicate rather than as two sends. */}
        <Chip size="small" variant="outlined" label={shortChannelLabel(recipient.channel || 'email')} />
        {/* status = did the provider accept it. The delivery chip beside it is
            what the provider later reported, and is absent (not "failed") when
            there is no report. */}
        <Chip size="small" label={recipient.status} color={statusColor(recipient.status)} />
        {delivery ? (
          <Tooltip title={delivery.tooltip ?? ''} disableHoverListener={!delivery.tooltip}>
            <Chip size="small" variant={delivery.color === 'default' ? 'outlined' : 'filled'} label={delivery.label} color={delivery.color} />
          </Tooltip>
        ) : null}
      </Stack>
    </Stack>
  );
}

function BroadcastHistoryCard({
  broadcast,
  timeZone,
  reportsExpected,
  onReuse,
}: {
  broadcast: GroupBroadcastSummary;
  /** The trip's zone: the same one the Scheduled list above prints, so the page never mixes two. */
  timeZone: string;
  reportsExpected: boolean;
  onReuse: (past: GroupBroadcastSummary) => void;
}) {
  const [showRecipients, setShowRecipients] = useState(false);
  // Trip time with the zone named, exactly like the Scheduled list. The browser's own zone made one page read in two.
  const sentAt = formatInZone(broadcast.createdAt, timeZone, '');
  // Failures and skips are per (guest, channel) exactly as the sent counts are.
  // A blended "3 failed" beside "42 email · 18 SMS" cannot tell a host whether
  // a single text failed, and both numbers are on the same payload.
  const failureLine = historyFailureLine(broadcast);
  const skippedLine = historySkippedLine(broadcast);
  // One row per (guest, channel) since SMS shipped, so counting rows would read
  // 2x on a dual-channel send and imply twice the roster.
  const guestCount = distinctRecipientCount(broadcast.recipients);
  const deliveryLine = deliverySummaryLine(broadcast.recipients, reportsExpected);
  const failureBadges = deliveryFailureBadges(broadcast.recipients);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>{broadcast.subject}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {sentAt ? `${sentAt} · ` : ''}
            {broadcast.segment ? `${audienceLabel(broadcast.segment)} · ` : 'All guests · '}
            {historySentLine(broadcast)}
          </Typography>
          {failureLine ? (
            <Typography variant="caption" color="error" display="block">{failureLine}</Typography>
          ) : null}
          {skippedLine ? (
            <Typography variant="caption" color="text.secondary" display="block">{skippedLine}</Typography>
          ) : null}
          {deliveryLine ? (
            <Typography variant="caption" color="text.secondary" display="block">{deliveryLine}</Typography>
          ) : null}
          {failureBadges.length > 0 ? (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
              {failureBadges.map((badge) => (
                <Chip key={badge} size="small" color="warning" label={badge} />
              ))}
            </Stack>
          ) : null}
        </Box>
        <Button size="small" variant="outlined" sx={{ flexShrink: 0 }} onClick={() => onReuse(broadcast)}>
          Reuse
        </Button>
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
      >
        {broadcast.body}
      </Typography>
      {normalizeChannels(broadcast.channels).includes('sms') && broadcast.smsBody ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', whiteSpace: 'pre-wrap' }}>
          SMS: {broadcast.smsBody}
        </Typography>
      ) : null}
      {broadcast.recipients.length > 0 ? (
        <>
          <Button size="small" sx={{ mt: 1 }} onClick={() => setShowRecipients((prev) => !prev)}>
            {showRecipients ? 'Hide recipients' : `Who it went to (${guestCount})`}
          </Button>
          <Collapse in={showRecipients} unmountOnExit>
            <Divider sx={{ my: 1 }} />
            <Stack divider={<Divider flexItem />} spacing={0.5}>
              {broadcast.recipients.map((recipient, index) => (
                <RecipientRow
                  key={`${recipient.inviteeId || recipient.email || recipient.phone || 'r'}-${recipient.channel || 'email'}-${index}`}
                  recipient={recipient}
                  reportsExpected={reportsExpected}
                />
              ))}
            </Stack>
          </Collapse>
        </>
      ) : null}
    </Paper>
  );
}

export default function BroadcastHistory({
  groupId,
  timeZone: groupTimeZone,
  onReuse,
}: {
  groupId: string;
  /** The group's `timeZone`; resolved here the same way the Scheduled list resolves it. */
  timeZone?: string | null;
  onReuse: (past: GroupBroadcastSummary) => void;
}) {
  const { timeZone } = resolveTripTimeZone(groupTimeZone);
  const { data, isLoading, error } = useQuery(
    BROADCAST_HISTORY_KEY(groupId),
    () => groupsApi.listBroadcasts(groupId),
    {
      staleTime: 30_000,
      // Late delivery reports for a fresh send; false (no polling) otherwise.
      // react-query already pauses the interval while the tab is hidden.
      refetchInterval: (history) => deliveryRefetchInterval(history),
    },
  );
  const reportsExpected = deliveryReportsExpected(historyRecipients(data));
  const reportsOffNotice = deliveryReportsOffNotice(data);

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Sent history</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every broadcast you've sent, newest first — expand one to see who it went to and whether it arrived.
      </Typography>
      {reportsOffNotice ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0 }}>{reportsOffNotice}</Alert>
      ) : null}
      {isLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} /><Typography color="text.secondary">Loading history…</Typography>
        </Stack>
      ) : error ? (
        <Alert severity="error">Couldn't load the broadcast history.</Alert>
      ) : !data || data.length === 0 ? (
        <Typography color="text.secondary">No broadcasts sent yet.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {data.map((broadcast) => (
            <BroadcastHistoryCard key={broadcast.id} broadcast={broadcast} timeZone={timeZone} reportsExpected={reportsExpected} onReuse={onReuse} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}
